import { ChevronRight } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import React, { createContext, useContext, useLayoutEffect, useMemo, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { cn } from '@column/ui';

import { ROUTE_PREFIX } from '~/app/routes';
import { type PageType, Sitemap } from '~/app/Sitemap';
import { useNavigate } from '~/lib/navigation';

type BreadcrumbTitleApi = {
  setTitle: (title: string | null) => void;
  claim: () => () => void;
};
const BreadcrumbTitleContext = createContext<BreadcrumbTitleApi | null>(null);

type BreadcrumbTitleState = { title: string | null; claimed: boolean };
const BreadcrumbTitleOverrideContext = createContext<BreadcrumbTitleState>({ title: null, claimed: false });

export const BreadcrumbTitleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [title, setTitle] = useState<string | null>(null);
  const [claims, setClaims] = useState(0);
  const api = useMemo<BreadcrumbTitleApi>(
    () => ({
      setTitle,
      claim: () => {
        setClaims((c) => c + 1);
        return () => setClaims((c) => Math.max(0, c - 1));
      },
    }),
    []
  );
  const value = useMemo<BreadcrumbTitleState>(() => ({ title, claimed: claims > 0 }), [title, claims]);
  return (
    <BreadcrumbTitleContext.Provider value={api}>
      <BreadcrumbTitleOverrideContext.Provider value={value}>{children}</BreadcrumbTitleOverrideContext.Provider>
    </BreadcrumbTitleContext.Provider>
  );
};

export const useBreadcrumbTitle = (title: string | undefined | null) => {
  const ctx = useContext(BreadcrumbTitleContext);
  useLayoutEffect(() => {
    if (!ctx) return;
    return ctx.claim();
  }, [ctx]);
  useLayoutEffect(() => {
    if (title != null) ctx?.setTitle(title);
    return () => ctx?.setTitle(null);
  }, [title, ctx]);
};

const useBreadcrumbTrail = () => {
  const location = useLocation();
  const { title: titleOverride, claimed } = useContext(BreadcrumbTitleOverrideContext);

  return useMemo(() => {
    const base = findBreadcrumbTrail(location.pathname, Sitemap, ROUTE_PREFIX) ?? [];
    if (base.length === 0) return base;

    const lastIndex = base.length - 1;
    const isAwaitingTitle =
      titleOverride == null && (base[lastIndex].awaitingTitle || (claimed && base[lastIndex].path.includes(':')));

    return base.map((seg, i) => {
      if (i !== lastIndex) return seg;
      return { ...seg, title: titleOverride ?? seg.title, awaitingTitle: isAwaitingTitle };
    });
  }, [location.pathname, titleOverride, claimed]);
};

// Asymmetric: enter takes a beat longer with ease-out (deliberate), exit is
// shorter with ease-in (soft, doesn't fight for attention).
const ENTER_TRANSITION = { duration: 0.18, ease: [0.32, 0.72, 0, 1] as const };
const EXIT_TRANSITION = { duration: 0.12, ease: [0.4, 0, 1, 1] as const };

type BreadcrumbSegment = {
  title: string;
  path: string;
  awaitingTitle?: boolean;
};

const hasChildPages = (
  page: PageType<'top'> | PageType<'nested'>
): page is PageType<'top'> & { childPages: PageType<'nested'>[] } =>
  'childPages' in page && Array.isArray(page.childPages);

const resolveUrl = (page: PageType<'top'> | PageType<'nested'>): string =>
  typeof page.url === 'string' ? page.url : page.url.route;

const isParameterized = (url: string) => url.includes(':');

const buildFullPath = (segment: string, parentPath: string) =>
  segment.startsWith('/') ? `${ROUTE_PREFIX}${segment}` : `${parentPath}/${segment}`.replace(/\/+/g, '/');

const resolveBreadcrumbPath = (page: PageType<'top'> | PageType<'nested'>, fullPath: string): string => {
  if (hasChildPages(page) && !page.element && page.childPages.length > 0) {
    return buildFullPath(resolveUrl(page.childPages[0]), fullPath);
  }
  return fullPath;
};

const findBreadcrumbTrail = (
  pathname: string,
  pages: (PageType<'top'> | PageType<'nested'>)[],
  parentPath: string
): BreadcrumbSegment[] | null => {
  let paramMatch: BreadcrumbSegment[] | null = null;

  for (const page of pages) {
    const segment = resolveUrl(page);
    const fullPath = buildFullPath(segment, parentPath);

    if (hasChildPages(page)) {
      const prefixMatch = matchPath({ path: fullPath, end: false }, pathname);
      if (prefixMatch) {
        const childTrail = findBreadcrumbTrail(pathname, page.childPages, fullPath);
        if (childTrail) {
          const trail =
            page.showInBreadcrumbs === false
              ? childTrail
              : [
                  {
                    title: page.title,
                    path: resolveBreadcrumbPath(page, fullPath),
                    awaitingTitle: page.awaitingTitle,
                  },
                  ...childTrail,
                ];
          if (!isParameterized(segment)) return trail;
          paramMatch ??= trail;
          continue;
        }
      }
    }

    if (matchPath(fullPath, pathname)) {
      if (page.visibility === 'tab') {
        if (page.showInBreadcrumbs === false) return [];
        return segment ? [{ title: page.title, path: fullPath, awaitingTitle: page.awaitingTitle }] : [];
      }
      const trail = [
        { title: page.title, path: resolveBreadcrumbPath(page, fullPath), awaitingTitle: page.awaitingTitle },
      ];
      if (!isParameterized(segment)) return trail;
      paramMatch ??= trail;
    }
  }

  return paramMatch;
};

const AnimatedBreadcrumbText: React.FC<{
  children: string;
  isPage?: boolean;
  onClick?: () => void;
}> = ({ children, isPage, onClick }) => (
  <span className="relative inline-flex min-w-0 max-w-full overflow-hidden">
    <AnimatePresence initial={false}>
      <motion.span
        key={children}
        initial={{ opacity: 0, x: -4 }}
        animate={{ opacity: 1, x: 0, transition: ENTER_TRANSITION }}
        exit={{ opacity: 0, x: 4, position: 'absolute', transition: EXIT_TRANSITION }}
        className={cn(
          isPage
            ? 'block max-w-[min(60vw,38rem)] truncate font-medium text-foreground md:max-w-[min(45vw,46rem)]'
            : 'transition-colors hover:text-foreground',
          onClick && 'cursor-pointer'
        )}
        onClick={onClick}
      >
        {children}
      </motion.span>
    </AnimatePresence>
  </span>
);

export const PageBreadcrumbs: React.FC<{ headerDecoration?: React.ReactNode; headerDecorationKey?: React.Key }> = ({
  headerDecoration,
  headerDecorationKey,
}) => {
  const navigate = useNavigate();
  const trail = useBreadcrumbTrail();

  if (trail.length === 0) return null;

  return (
    <nav aria-label="breadcrumb">
      <ol className="flex min-w-0 items-center gap-0 text-sm font-medium whitespace-nowrap text-muted-foreground">
        <AnimatePresence initial={false}>
          {trail.map((segment, i) => {
            const isLast = i === trail.length - 1;
            if (isLast && segment.awaitingTitle) return null;

            return (
              <motion.li
                key={i}
                initial={{ opacity: 0, width: 0, clipPath: 'inset(0 100% 0 0)' }}
                animate={{
                  opacity: 1,
                  width: 'auto',
                  clipPath: 'inset(0 0% 0 0)',
                  transition: ENTER_TRANSITION,
                }}
                exit={{
                  opacity: 0,
                  width: 0,
                  clipPath: 'inset(0 100% 0 0)',
                  transition: EXIT_TRANSITION,
                }}
                className={cn(
                  'inline-flex min-w-0 items-center justify-start overflow-hidden',
                  !isLast && 'hidden md:inline-flex'
                )}
                data-cy={isLast && !segment.awaitingTitle ? `Header: ${segment.title}` : undefined}
              >
                <div className="inline-flex min-w-max items-center">
                  {i > 0 && (
                    <span className="hidden px-1.5 md:flex [&>svg]:size-3.5" aria-hidden>
                      <ChevronRight className="size-3.5" />
                    </span>
                  )}
                  <AnimatedBreadcrumbText isPage={isLast} onClick={!isLast ? () => navigate(segment.path) : undefined}>
                    {segment.title}
                  </AnimatedBreadcrumbText>
                  {isLast && headerDecoration ? (
                    <span key={headerDecorationKey} className="ml-2 flex shrink-0 items-center gap-1">
                      {headerDecoration}
                    </span>
                  ) : null}
                </div>
              </motion.li>
            );
          })}
        </AnimatePresence>
      </ol>
    </nav>
  );
};
